import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Formateadores numéricos y de fecha
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: 'ARS',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount || 0);
};

const formatQuantity = (amount) => {
    return new Intl.NumberFormat('es-AR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount || 0);
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const parts = String(dateStr).split('T')[0].split('-');
    if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
};

const getIvaLabel = (c_iva) => {
    const mapping = {
        1: 'Resp. Inscripto',
        2: 'Resp. No Inscripto',
        3: 'Consumidor Final',
        4: 'Exento',
        5: 'Monotributo',
        6: 'No Responsable'
    };
    return mapping[c_iva] || (c_iva ? `Condición ${c_iva}` : 'Consumidor Final');
};

/**
 * Crea la instancia jsPDF configurada con todo el diseño comercial del presupuesto.
 */
export const generateQuotePDF = (quote, user = {}, sellers = []) => {
    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 12;
    const contentWidth = pageWidth - (margin * 2);

    // Colores del tema
    const primaryColor = [26, 54, 93];     // Navy (#1a365d)
    const secondaryColor = [71, 85, 105];  // Slate (#475569)
    const lightBg = [248, 250, 252];       // Light Gray (#f8fafc)
    const borderColor = [226, 232, 240];   // Border (#e2e8f0)
    const darkText = [15, 23, 42];         // Dark text (#0f172a)

    // Datos de la empresa activa
    const empresaNombre = user?.empresa_activa?.nombre || 'SISGECOM';
    const numPresupuesto = quote.n_presu ? String(quote.n_presu).padStart(8, '0') : String(quote.id || '00000000').padStart(8, '0');
    const fechaPresu = formatDate(quote.f_presu || quote.f_compr || quote.f_ingre || new Date().toISOString());
    const horaPresu = quote.h_compr || quote.h_entre || quote.h_ingre || '';

    // Obtener nombre del vendedor
    let vendedorText = quote.vendedor_nombre || '';
    if (!vendedorText && quote.c_vende) {
        if (sellers && sellers.length > 0) {
            const found = sellers.find(s => String(s.c_vende).trim() === String(quote.c_vende).trim());
            if (found) {
                const ape = (found.d_apell || '').trim();
                const nom = (found.d_nombr || '').trim();
                if (ape && nom) vendedorText = `${ape}, ${nom}`;
                else vendedorText = ape || nom || `Vendedor ${quote.c_vende}`;
            }
        }
        if (!vendedorText) {
            vendedorText = `Vendedor ${quote.c_vende}`;
        }
    }
    if (!vendedorText) {
        vendedorText = quote.c_usuar || 'General';
    }

    // ==========================================
    // 1. ENCABEZADO COMERCIAL (MEMBRETE)
    // ==========================================
    // Barra decorativa superior
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 4, 'F');

    let currentY = margin + 2;

    // Columna Izquierda: Empresa
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...primaryColor);
    doc.text(empresaNombre.toUpperCase(), margin, currentY + 4);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...secondaryColor);
    doc.text('Sistema Integrado de Gestión Comercial', margin, currentY + 9);
    doc.text('Documento no válido como factura comercial', margin, currentY + 13);

    // Columna Derecha: Recuadro del Presupuesto
    const boxWidth = 72;
    const boxX = pageWidth - margin - boxWidth;
    
    doc.setFillColor(...lightBg);
    doc.setDrawColor(...borderColor);
    doc.roundedRect(boxX, currentY - 2, boxWidth, 23, 2, 2, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...primaryColor);
    doc.text('PRESUPUESTO', boxX + (boxWidth / 2), currentY + 3.5, { align: 'center' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...darkText);
    doc.text(`Nº PRE-${numPresupuesto}`, boxX + (boxWidth / 2), currentY + 9, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...secondaryColor);
    const dateText = horaPresu ? `Fecha: ${fechaPresu}  Hora: ${horaPresu}` : `Fecha: ${fechaPresu}`;
    doc.text(dateText, boxX + (boxWidth / 2), currentY + 14, { align: 'center' });
    
    if (quote.n_lista) {
        doc.text(`Lista de Precios: Lista ${quote.n_lista}`, boxX + (boxWidth / 2), currentY + 18.5, { align: 'center' });
    }

    currentY += 25;

    // ==========================================
    // 2. FICHA DE DATOS DEL CLIENTE Y OPERACIÓN
    // ==========================================
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...borderColor);
    doc.roundedRect(margin, currentY, contentWidth, 26, 1.5, 1.5, 'FD');

    // Título de la sección
    doc.setFillColor(...primaryColor);
    doc.roundedRect(margin, currentY, contentWidth, 5.5, 1.5, 1.5, 'F');
    // Rellenar las esquinas inferiores del header para que sean cuadradas
    doc.rect(margin, currentY + 3.5, contentWidth, 2, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text('DATOS DEL CLIENTE Y FACTURACIÓN', margin + 3, currentY + 4);

    const clientCol1X = margin + 4;
    const clientCol2X = margin + (contentWidth / 2) + 2;
    let clientY = currentY + 10;

    // Fila 1
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...secondaryColor);
    doc.text('Cliente / Razón Social:', clientCol1X, clientY);
    
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkText);
    const clientLabel = quote.c_clien ? `[${quote.c_clien}] ${quote.d_razon || 'Cliente General'}` : (quote.d_razon || 'Cliente General');
    doc.text(clientLabel.substring(0, 48), clientCol1X + 33, clientY);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...secondaryColor);
    doc.text('CUIT / Documento:', clientCol2X, clientY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkText);
    doc.text(quote.n_cuit ? String(quote.n_cuit) : 'Sin registrar', clientCol2X + 28, clientY);

    // Fila 2
    clientY += 5;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...secondaryColor);
    doc.text('Domicilio / Localidad:', clientCol1X, clientY);
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkText);
    const domLoc = [quote.d_entre || quote.d_domic, quote.d_local].filter(Boolean).join(', ') || 'No especificado';
    doc.text(domLoc.substring(0, 48), clientCol1X + 33, clientY);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...secondaryColor);
    doc.text('Condición de IVA:', clientCol2X, clientY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkText);
    doc.text(getIvaLabel(quote.c_iva), clientCol2X + 28, clientY);

    // Fila 3
    clientY += 5;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...secondaryColor);
    doc.text('Contacto / Teléfono:', clientCol1X, clientY);
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkText);
    const phones = [quote.d_whatsapp, quote.n_celul, quote.n_telef].filter(Boolean).join(' / ') || 'Sin teléfono';
    doc.text(phones.substring(0, 48), clientCol1X + 33, clientY);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...secondaryColor);
    doc.text('Vendedor:', clientCol2X, clientY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkText);
    doc.text(vendedorText.substring(0, 32), clientCol2X + 28, clientY);

    currentY += 29;

    // ==========================================
    // 3. TABLA DETALLADA DE ARTÍCULOS
    // ==========================================
    const items = quote.items || [];
    const tableBody = items.map((it, idx) => {
        const linePrice = parseFloat(it.i_unita || it.i_lista || it.i_publi || it.i_campre || it.price || 0);
        const lineQty = parseFloat(it.q_artic || it.quantity || 1);
        const lineBoxes = it.q_bulto || it.boxes || 0;
        const lineDisc = parseFloat(it.p_desc2 || it.p_descu || it.discountSurcharge || 0);
        const lineTotal = (it.i_venta && parseFloat(it.i_venta) > 0)
            ? parseFloat(it.i_venta)
            : (it.i_total !== undefined && parseFloat(it.i_total) > 0)
                ? parseFloat(it.i_total)
            : (it.c_desrec === 'R' ? (linePrice * lineQty * (1 + Math.abs(lineDisc) / 100)) : (linePrice * lineQty * (1 - Math.abs(lineDisc) / 100)));

        let descText = it.d_artic || it.name || 'Artículo sin descripción';
        if (it.d_obser) {
            descText += `\n(${it.d_obser})`;
        }

        return [
            idx + 1,
            it.c_artic || it.id || '-',
            descText,
            formatQuantity(lineQty),
            lineBoxes > 0 ? lineBoxes : '-',
            formatCurrency(linePrice),
            lineDisc !== 0 ? `${it.c_desrec === 'R' ? '+' : (it.c_desrec === 'D' ? '-' : (lineDisc < 0 ? '-' : '+'))}${Math.abs(lineDisc)}%` : '-',
            formatCurrency(lineTotal)
        ];
    });

    if (tableBody.length === 0) {
        tableBody.push(['-', '-', 'No hay artículos cargados en este presupuesto', '-', '-', '-', '-', '-']);
    }

    autoTable(doc, {
        startY: currentY,
        head: [['#', 'Código', 'Descripción del Artículo', 'Cant.', 'Bultos', 'P. Unitario', 'Desc./Rec.', 'Total']],
        body: tableBody,
        theme: 'plain',
        styles: {
            fontSize: 8,
            cellPadding: 2,
            textColor: darkText,
            lineColor: borderColor,
            lineWidth: 0.2,
            valign: 'middle'
        },
        headStyles: {
            fillColor: primaryColor,
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'center',
            fontSize: 8
        },
        columnStyles: {
            0: { halign: 'center', cellWidth: 8 },
            1: { halign: 'left', cellWidth: 26, fontStyle: 'bold', textColor: primaryColor },
            2: { halign: 'left' },
            3: { halign: 'right', cellWidth: 16, fontStyle: 'bold' },
            4: { halign: 'center', cellWidth: 14 },
            5: { halign: 'right', cellWidth: 24 },
            6: { halign: 'center', cellWidth: 18 },
            7: { halign: 'right', cellWidth: 26, fontStyle: 'bold' }
        },
        alternateRowStyles: {
            fillColor: [253, 248, 236] // Tono sutil cálido/beige similar a la UI
        },
        margin: { left: margin, right: margin, bottom: 42 }
    });

    // ==========================================
    // 4. PIE DE TOTALES, IMPUESTOS Y OBSERVACIONES
    // ==========================================
    const finalY = doc.lastAutoTable.finalY + 4;
    
    // Si no queda suficiente espacio en la página actual para el pie, agregar nueva página
    let footerY = finalY;
    if (footerY + 38 > pageHeight - 15) {
        doc.addPage();
        footerY = margin + 5;
    }

    const totalsBoxWidth = 85;
    const totalsBoxX = pageWidth - margin - totalsBoxWidth;
    const obsBoxWidth = contentWidth - totalsBoxWidth - 4;

    // --- Recuadro de Observaciones (Izquierda) ---
    doc.setFillColor(...lightBg);
    doc.setDrawColor(...borderColor);
    doc.roundedRect(margin, footerY, obsBoxWidth, 34, 1.5, 1.5, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...secondaryColor);
    doc.text('OBSERVACIONES / NOTAS:', margin + 3, footerY + 4.5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...darkText);
    
    const obsText = quote.d_obser || 'Presupuesto válido por 7 días hábiles sujeto a disponibilidad de stock y variación de listas de precios.';
    const splitObs = doc.splitTextToSize(obsText, obsBoxWidth - 6);
    doc.text(splitObs, margin + 3, footerY + 9);

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(6.5);
    doc.setTextColor(...secondaryColor);
    doc.text('* Los precios no incluyen flete ni cargos adicionales salvo indicación expresa.', margin + 3, footerY + 30);

    // --- Recuadro de Totales e Impuestos (Derecha) ---
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...borderColor);
    doc.roundedRect(totalsBoxX, footerY, totalsBoxWidth, 34, 1.5, 1.5, 'FD');

    const subtotalNeto = parseFloat(quote.i_grava) || 0;
    const noGravado = parseFloat(quote.i_nogra) || 0;
    const totalIVA = (parseFloat(quote.i_iva1) || 0) + (parseFloat(quote.i_iva2) || 0) + (parseFloat(quote.i_iva3) || 0) + (parseFloat(quote.i_ivaimp) || 0);
    const totalIIBB = parseFloat(quote.i_ingbru) || 0;
    const totalTasas = (parseFloat(quote.i_munic) || 0) + (parseFloat(quote.i_impub) || 0) + (parseFloat(quote.i_imint) || 0);
    const totalFinal = parseFloat(quote.i_total) || (subtotalNeto + noGravado + totalIVA + totalIIBB + totalTasas);

    let totLineY = footerY + 5;

    // Subtotal
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...secondaryColor);
    doc.text('Subtotal Neto / Gravado:', totalsBoxX + 3, totLineY);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkText);
    doc.text(formatCurrency(subtotalNeto), totalsBoxX + totalsBoxWidth - 3, totLineY, { align: 'right' });

    // IVA (si aplica)
    if (totalIVA > 0) {
        totLineY += 4.5;
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...secondaryColor);
        doc.text('IVA Liquidado:', totalsBoxX + 3, totLineY);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...darkText);
        doc.text(formatCurrency(totalIVA), totalsBoxX + totalsBoxWidth - 3, totLineY, { align: 'right' });
    }

    // IIBB / Otros (si aplica)
    if (totalIIBB > 0 || totalTasas > 0) {
        totLineY += 4.5;
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...secondaryColor);
        doc.text('Percepciones e Impuestos:', totalsBoxX + 3, totLineY);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...darkText);
        doc.text(formatCurrency(totalIIBB + totalTasas), totalsBoxX + totalsBoxWidth - 3, totLineY, { align: 'right' });
    }

    // Bloque destacado TOTAL
    const totalBannerY = footerY + 23;
    doc.setFillColor(...primaryColor);
    doc.roundedRect(totalsBoxX + 2, totalBannerY, totalsBoxWidth - 4, 9, 1.5, 1.5, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.text('TOTAL FINAL:', totalsBoxX + 5, totalBannerY + 6);
    doc.setFontSize(11);
    doc.text(formatCurrency(totalFinal), totalsBoxX + totalsBoxWidth - 5, totalBannerY + 6, { align: 'right' });

    // ==========================================
    // 5. NUMERACIÓN DE PÁGINAS Y FOOTER
    // ==========================================
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(...secondaryColor);
        
        // Línea divisoria pie de página
        doc.setDrawColor(...borderColor);
        doc.setLineWidth(0.3);
        doc.line(margin, pageHeight - 8, pageWidth - margin, pageHeight - 8);

        doc.text(`${empresaNombre} - Presupuesto Nº ${numPresupuesto}`, margin, pageHeight - 4.5);
        doc.text(`Página ${i} de ${totalPages}`, pageWidth - margin, pageHeight - 4.5, { align: 'right' });
    }

    return doc;
};

/**
 * Descarga directamente el archivo PDF en el navegador del cliente.
 */
export const downloadQuotePDF = (quote, user, sellers = []) => {
    try {
        const doc = generateQuotePDF(quote, user, sellers);
        const numPresupuesto = quote.n_presu ? String(quote.n_presu).padStart(8, '0') : String(quote.id || '00000000').padStart(8, '0');
        const filename = `Presupuesto_PRE-${numPresupuesto}.pdf`;
        doc.save(filename);
        return true;
    } catch (error) {
        console.error('Error al generar / descargar PDF del presupuesto:', error);
        throw error;
    }
};

/**
 * Abre el PDF en una ventana/pestaña para previsualizarlo o imprimirlo directamente con el diálogo del navegador.
 */
export const printQuotePDF = (quote, user, sellers = []) => {
    try {
        const doc = generateQuotePDF(quote, user, sellers);
        const blobUrl = doc.output('bloburl');
        const printWindow = window.open(blobUrl, '_blank');
        if (printWindow) {
            printWindow.focus();
        }
        return true;
    } catch (error) {
        console.error('Error al imprimir PDF del presupuesto:', error);
        throw error;
    }
};

/**
 * Retorna el PDF como Blob para adjuntar en envío de correos o procesamientos locales.
 */
export const getQuotePDFBlob = (quote, user, sellers = []) => {
    const doc = generateQuotePDF(quote, user, sellers);
    return doc.output('blob');
};

/**
 * Retorna el PDF en formato Base64 para adjuntar al backend.
 */
export const getQuotePDFBase64 = (quote, user, sellers = []) => {
    try {
        const doc = generateQuotePDF(quote, user, sellers);
        const dataUri = doc.output('datauristring');
        return dataUri ? dataUri.split(',')[1] : '';
    } catch (e) {
        console.error('Error generando base64 del PDF:', e);
        return '';
    }
};


